import type { TFunction } from 'i18next';
import type {
  AssignmentStatus,
  ChangeMyStatusDto,
  RecurrenceRuleDto,
} from '@gokrew/models/tasks';
import type { ComboBoxOption } from '@/components/ComboBox';
import { formatDate } from './utility';

export const TERMINAL_TASK_STATUSES: AssignmentStatus[] = [
  'approved',
  'completed',
];

export function isTerminalTaskStatus(status: AssignmentStatus): boolean {
  return TERMINAL_TASK_STATUSES.includes(status);
}

export type TaskStatBucket = 'pending' | 'in_progress' | 'completed' | 'overdue';

// stat card's count always matches what clicking it filters the list to.
export const TASK_STAT_BUCKET_STATUSES: Record<
  Exclude<TaskStatBucket, 'overdue'>,
  AssignmentStatus[]
> = {
  pending: ['not_started'],
  in_progress: ['in_progress', 'submitted'],
  completed: ['approved', 'completed'],
};

/** Which stat bucket (if any) the current status/overdue filter state matches. */
export function getActiveTaskStatBucket(
  statusIds: string[],
  overdueOnly: boolean
): TaskStatBucket | undefined {
  if (overdueOnly) return 'overdue';
  if (!statusIds.length) return undefined;
  const sorted = [...statusIds].sort().join(',');
  return (
    Object.keys(TASK_STAT_BUCKET_STATUSES) as Exclude<TaskStatBucket, 'overdue'>[]
  ).find(
    (bucket) => [...TASK_STAT_BUCKET_STATUSES[bucket]].sort().join(',') === sorted
  );
}

/**
 * Toggling a stat card is exclusive: selecting one bucket clears the other
 * filter dimension (status vs. overdueOnly) so the list always matches
 * exactly what the clicked card counted. Clicking the active card clears both.
 */
export function buildTaskStatBucketFilter(
  bucket: TaskStatBucket,
  activeBucket: TaskStatBucket | undefined,
  t: TFunction
): { status: ComboBoxOption[]; overdueOnly: boolean } {
  if (activeBucket === bucket) {
    return { status: [], overdueOnly: false };
  }
  if (bucket === 'overdue') {
    return { status: [], overdueOnly: true };
  }
  return {
    status: TASK_STAT_BUCKET_STATUSES[bucket].map((status) => ({
      label: t(`tasks.status.${status}`),
      value: status,
    })),
    overdueOnly: false,
  };
}

// Branches on the OCCURRENCE snapshot of requiresApproval, mirroring the
// backend's TRANSITIONS table (task-status.service.ts) exactly — sending the
// wrong action for the wrong path is a hard 400 there, not a soft mismatch.
export function getNextTaskStatusAction(
  status: AssignmentStatus,
  requiresApproval: boolean,
  isFlowCompleted = false
): { labelKey: string; value: ChangeMyStatusDto['status'] } | undefined {
  if (status === 'not_started') {
    return { labelKey: 'dialog.startTask', value: 'in_progress' };
  }
  if (status === 'in_progress') {
    return requiresApproval
      ? { labelKey: 'dialog.submitTask', value: 'submitted' }
      : { labelKey: 'dialog.completeTask', value: 'completed' };
  }
  // Simple-path only: the assignee may revert a completed task themselves —
  // unless the parent flow has itself been marked completed by an admin
  if (status === 'completed' && !requiresApproval && !isFlowCompleted) {
    return { labelKey: 'dialog.uncompleteTask', value: 'in_progress' };
  }
  return undefined;
}

export function buildTaskRecurrenceSummary(
  rule: RecurrenceRuleDto | null | undefined,
  t: TFunction,
  language: string
): string | null {
  if (!rule) return null;
  const freq = rule.recurFrequency;
  const interval = rule.recurInterval;

  const translatedFreq =
    freq === 'daily'
      ? t('time.days')
      : freq === 'weekly'
        ? t('time.weeks')
        : t('time.months');

  const base =
    interval === 1
      ? freq.charAt(0).toUpperCase() + freq.slice(1)
      : t('dialog.every', { interval, freq: translatedFreq });

  const end =
    rule.recurEndType === 'never'
      ? t('dialog.noEnd')
      : rule.recurEndType === 'after'
        ? t('dialog.occurrences', { count: rule.recurEndCount })
        : rule.recurEndOn
          ? t('dialog.until', {
              date: formatDate(rule.recurEndOn, {
                format: 'medium',
                locale: language,
              }),
            })
          : '';
  return end ? `${base} · ${end}` : base;
}
